import SessionController from '@/controllers/SessionController';
import MetricController from '@/controllers/MetricController';
import utils from '@/utils';
import jwt from 'jsonwebtoken';
import { axiosApiInstance } from '@/utils/axios';
import { getInstance } from '@/auth';

const initialState = {
  token: localStorage.getItem('login-token') || '',
  realm: localStorage.getItem('login-realm') || 'https://api.fabric.abysssolutions.co',
  status: '',
  error: '',
  attempt: false,
  sphericalViewSelectedEquipment: {
    selectedEquipment: {},
    selectedPolygon: {},
  },
  username: '',
  roles: [],
  id: '',
};

// getters
const getters = {
  isAuthenticated: (state) => (state.token ? !!state.token : localStorage.getItem('login-token')),
  attempt: (state) => state.attempt,
  status: (state) => state.status,
  error: (state) => state.error,
  defaultRealm: (state) => state.realm === 'https://api.fabric.abysssolutions.co',
  username: (state) => state.username,
  roles: (state) => state.roles,
  id: (state) => state.id,
};

// actions
const actions = {
  authenticate: async ({ commit }, creds) => {
    // timeout auth in 10s
    const timeout = 10000;
    const { promiseOrTimeout, timeoutId } = utils.promiseWithTimeout(
      SessionController.authenticate(creds.username, creds.password),
      timeout,
      'authenticate'
    );
    commit('authAttempt');
    try {
      const result = await promiseOrTimeout;
      commit('authSuccess');
      localStorage.setItem('login-token', result.data.token);
      axiosApiInstance.defaults.headers.common.Authorization = `Bearer ${result.data.token}`;
      const metricResult = await MetricController.getMetricData(jwt.decode(result.data.token).roles[0]);
      localStorage.setItem('locale', JSON.stringify(metricResult.data[0].data.locale));
      this.setDbAuthVals(result.data.token);
      commit('authSuccess', result.data.token);
    } catch (error) {
      commit('authError', error);
    } finally {
      window.clearTimeout(timeoutId);
    }
  },
  setDbAuthVals: ({ commit }, auth0Token) => {
    console.log('decoding');
    const { username, roles, _id: id } = jwt.decode(auth0Token);
    commit('setVals', {
      username,
      roles,
      id,
    });
  },
  auth0Authenticate: async ({ commit }, auth0Token) => {
    // timeout auth in 10s
    const timeout = 10000;
    const { promiseOrTimeout, timeoutId } = utils.promiseWithTimeout(
      SessionController.getUserInfo(auth0Token),
      timeout,
      'auth0Authenticate'
    );
    commit('authAttempt');
    try {
      const result = await promiseOrTimeout;
      commit('authSuccess');
      localStorage.setItem('login-token', auth0Token);
      axiosApiInstance.defaults.headers.common.Authorization = `Bearer ${auth0Token}`;
      const metricResult = await MetricController.getMetricData(result.roles[0]);
      localStorage.setItem('locale', JSON.stringify(metricResult.data[0].data.locale));
      commit('authSuccess', auth0Token);
      commit('setVals', {
        username: result.username,
        roles: result.roles,
        id: result._id,
      });
    } catch (error) {
      commit('authError', error);
    } finally {
      window.clearTimeout(timeoutId);
    }
  },

  deAuthenticate: ({ commit, dispatch }) => {
    dispatch('workpacks/reset', undefined, { root: true });
    commit('deauth');
    delete axiosApiInstance.defaults.headers.common.Authorization;
    localStorage.removeItem('login-token');
    localStorage.removeItem('locale');
  },

  setSelectedEquipment: ({ commit }, selectedEquipment) => {
    commit('setSelectedEquipment', selectedEquipment);
  },

  setSelectedPolygon: ({ commit }, selectedPolygon) => {
    commit('setSelectedPolygon', selectedPolygon);
  },

  initialiseAuthToken: async ({ dispatch }) => {
    const token = localStorage.getItem('login-token');
    if (token) {
      axiosApiInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
      await dispatch('auth0Authenticate', token);
    }

    axiosApiInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;
          const authService = getInstance();
          const accessToken = await authService.getTokenSilently();
          await dispatch('auth0Authenticate', accessToken);
          return axiosApiInstance(originalRequest);
        }
        return Promise.reject(error);
      }
    );
  },
  setError: ({ commit }, message) => {
    commit('setError', message);
  },
  clearError: ({ commit }) => {
    commit('setError', '');
  },
};

// mutations
const mutations = {
  authAttempt: (state) => {
    state.error = '';
    state.status = 'Logging in...';
    state.attempt = true;
  },

  deauth: (state) => {
    state.token = '';
    state.error = '';
    state.id = '';
    state.username = '';
    state.roles = [];
    state.status = 'Logged out';
  },

  authSuccess: (state, auth0Token) => {
    state.attempt = false;
    state.error = '';
    state.status = 'Success!, logged in';
    if (auth0Token) state.token = auth0Token;
  },
  authError: (state, error) => {
    state.attempt = false;
    state.status = '';
    state.error = error.response ? error.response.data.message : error.message;
  },
  setSelectedEquipment: (state, selectedEquipment) => {
    state.sphericalViewSelectedEquipment.selectedEquipment = selectedEquipment;
  },
  setSelectedPolygon: (state, selectedPolygon) => {
    state.sphericalViewSelectedEquipment.selectedPolygon = selectedPolygon;
  },
  setVals: (state, vals) => {
    state.username = vals.username;
    state.roles = vals.roles;
    state.id = vals.id;
  },
  setError: (state, message) => {
    state.error = message;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
